import { useState } from 'react';
import * as R from 'remeda';
import { useSelector } from 'react-redux';
import { Queue } from '@rossum/api-client/queues';
import { User } from '@rossum/api-client/users';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { IconFolder, IconInfoSquareRounded } from '@rossum/ui/icons/tabler';
import { List, Stack } from '@rossum/ui/material';
import { usePatchUser } from '../../../business/users/usePatchUser';
import InfoPlaceholder from '../../../components/UI/InfoPlaceHolder';
import { UserStats } from '../../../containers/Settings/containers/UserStats';
import {
  getRoleName,
  isUserAdminLike,
} from '../../../redux/modules/groups/helpers';
import { groupsSelector } from '../../../redux/modules/groups/selectors';
import { filterWorkspaces } from '../../queues/helpers/filterWorkspaces';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { WorkspaceWithQueues } from '../../queues/select-queue/types';
import {
  ToggleAccessToAllQueuesDialog,
  ToggleAllDialogAction,
} from '../components/ToggleAccessToAllQueuesDialog';
import { Workspace } from '../components/Workspace';
import { getQueueAndWorkspacesCounts } from '../helpers';

type AccessProps = {
  selectedUser: User;
};

const filterActiveQueues = (queues: Queue[], userQueues: string[]) =>
  queues.filter(({ url }) => userQueues.includes(url));

export const Access = ({ selectedUser }: AccessProps) => {
  const { workspacesWithQueues: workspaces, queues } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const [searchValue, setSearchValue] = useState('');

  const defaultDialogState = {
    workspaceName: '',
    workspaceQueues: [],
    action: undefined,
  };
  const [dialogState, setDialogState] = useState<{
    workspaceName: string;
    workspaceQueues: Queue[];
    action: ToggleAllDialogAction;
  }>(defaultDialogState);

  const groups = useSelector(groupsSelector);
  const role = getRoleName(selectedUser.groups, groups);
  const isAdmin = isUserAdminLike(role);

  const { mutate: patchUser } = usePatchUser();

  const { queuesCount, workspacesCount } = getQueueAndWorkspacesCounts(
    selectedUser,
    queues ?? [],
    isAdmin
  );

  if (!workspaces) {
    return null;
  }

  if (!workspaces.length || !selectedUser) {
    return (
      <>
        <UserStats
          queuesCount={queuesCount}
          workspacesCount={workspacesCount}
        />
        <InfoPlaceholder
          icon={IconFolder}
          title="containers.users.access.noQueuesFound"
        />
      </>
    );
  }
  const filteredWorkspaces = searchValue
    ? filterWorkspaces(searchValue, workspaces)
    : undefined;

  const resolvedWorkspaces = filteredWorkspaces || workspaces;

  return (
    <>
      <UserStats queuesCount={queuesCount} workspacesCount={workspacesCount} />
      {isAdmin ? (
        <InfoPlaceholder
          icon={IconInfoSquareRounded}
          text="containers.users.access.admin.text"
          title="containers.users.access.admin.title"
        />
      ) : (
        <div data-page-title="user-access">
          <Stack alignItems="flex-end">
            <SearchInput
              value={searchValue}
              onChange={value => setSearchValue(value)}
            />
          </Stack>
          {filteredWorkspaces && !filteredWorkspaces.length ? (
            <InfoPlaceholder
              icon={IconFolder}
              title="containers.users.access.noFilteredQueuesFound"
              text="containers.users.access.noFilteredQueuesFound.text"
            />
          ) : (
            <List dense>
              {resolvedWorkspaces.map(
                (workspace: WorkspaceWithQueues, index: number) =>
                  workspaces && (
                    <Workspace
                      key={workspace.id}
                      isFirst={index === 0}
                      activeQueuesCount={
                        filterActiveQueues(
                          workspace.queues,
                          selectedUser.queues
                        ).length
                      }
                      name={workspace.name}
                      onToggleAllClick={action =>
                        setDialogState({
                          workspaceName: workspace.name,
                          workspaceQueues: workspace.queues,
                          action,
                        })
                      }
                      forceExpand={searchValue !== ''}
                      queues={workspace.queues}
                      updateUserDetail={(payload: Partial<User>) =>
                        patchUser({
                          userId: selectedUser.id,
                          payload,
                        })
                      }
                      userQueues={selectedUser.queues}
                    />
                  )
              )}
            </List>
          )}

          <ToggleAccessToAllQueuesDialog
            action={dialogState.action}
            workspaceName={dialogState.workspaceName}
            onClose={() => setDialogState(defaultDialogState)}
            onSubmit={() => {
              if (dialogState.action === 'addAllQueues') {
                patchUser({
                  userId: selectedUser.id,
                  payload: {
                    queues: R.unique([
                      ...selectedUser.queues,
                      ...dialogState.workspaceQueues.map(({ url }) => url),
                    ]),
                  },
                });
              }

              if (dialogState.action === 'disableAllQueues') {
                patchUser({
                  userId: selectedUser.id,
                  payload: {
                    queues: selectedUser.queues.filter(
                      q =>
                        !dialogState.workspaceQueues.find(
                          ({ url }) => q === url
                        )
                    ),
                  },
                });
              }

              setDialogState(defaultDialogState);
            }}
          />
        </div>
      )}
    </>
  );
};
